import IconButton, { type IconButtonProps } from "@/components/ui/icon-button";
import type { IconProps } from "@phosphor-icons/react";
import { cva } from "class-variance-authority";
import {
  type FC,
  type ReactElement,
  cloneElement,
  isValidElement,
} from "react";

const labelVariants = cva(
  "uppercase font-semibold tracking-wide text-gray-600",
  {
    variants: {
      size: {
        sm: "text-[10px]",
        md: "text-xs",
        lg: "text-sm",
      },
    },
    defaultVariants: {
      size: "sm",
    },
  },
);

const iconSizes = {
  sm: 12,
  md: 14,
  lg: 16,
} as const;

/**
 * Props for the FilterHeader component.
 */
type FilterHeaderProps = {
  icon: ReactElement<IconProps>;
  label: string;
  actions?: IconButtonProps[];
  /**
   * The size of the icon (affects pixel size).
   * - "sm" = 12px
   * - "md" = 14px
   * - "lg" = 16px
   * @default "sm"
   */
  iconSize?: "sm" | "md" | "lg";
  /* Applies for label sizing */
  size?: "sm" | "md" | "lg";
};

/**
 * `FilterHeader` is a flexible header row component commonly used above
 * filter sections. It supports a left icon with label and optional action buttons
 * on the right. To be used in filter panels, settings sections, mainly for SOV.
 */
export const FilterHeader: FC<FilterHeaderProps> = ({
  icon,
  label,
  actions = [],
  iconSize = "sm",
  size = "sm",
}) => {
  const iconToRender = isValidElement(icon)
    ? cloneElement(icon, {
        size: icon.props.size || iconSizes[iconSize],
        className: "text-gray-600",
      })
    : null;

  return (
    <section
      className="flex items-center justify-between px-2"
      aria-labelledby="filter-header-label"
    >
      <div className="flex items-center gap-2 min-h-[32px]">
        <div aria-hidden="true">{iconToRender}</div>
        <div className={labelVariants({ size })} id="filter-header-label">
          {label}
        </div>
      </div>

      {/* Right side: action buttons */}
      <div
        className="flex items-center space-x-2"
        role="toolbar"
        aria-label="Filter actions"
      >
        {actions.map((actionProps) => (
          <IconButton key={actionProps.tooltipLabel} {...actionProps} />
        ))}
      </div>
    </section>
  );
};
